/**
 * Attendance as one list: who came (in now, out, or a day to fix) and the regulars who haven't come
 * in yet. Each row knows its one-tap action: Out for someone in, In for everyone else.
 */
import { stateOf, workLabel } from "./punchModel";

export type AttendanceKind = "IN" | "OUT" | "FIX" | "NOT_YET";

export type AttendanceRow = {
  personId: string;
  code: string;
  name: string;
  kind: AttendanceKind;
  /** e.g. "Helper on loom" or "Loom work"; for a regular, the work they came in for last time. */
  work: string;
  loomCode?: string | null;
  firstIn?: string | null;
  /** Out time and why, for someone who is out now. */
  lastOut?: string | null;
  outReason?: string | null;
  /** In since, for someone in now. */
  since?: string | null;
  minutes: number;
  pay?: string | null;
  noRate: boolean;
  waiting: boolean;
  /** A regular's last day here. */
  lastDay?: string | null;
  payBasis?: string | null;
  activityId?: string | null;
  /** The signed-in supervisor's own row: someone else marks it (the server refuses their own punches). */
  self?: boolean;
};

/** Shown on a supervisor's own row instead of In / Out / Switch / Undo. */
export const SELF_NOTE = "Another supervisor or admin marks your attendance";

const lastOf = <T,>(xs: T[] | undefined): T | undefined => (xs && xs.length ? xs[xs.length - 1] : undefined);

export const buildAttendanceRows = (data: any): AttendanceRow[] => {
  const mine = new Set<string>(data?.selfPersonIds || []);
  const came: AttendanceRow[] = (data?.people || []).map((p: any) => {
    const kind = stateOf(p) as AttendanceKind;
    const last = lastOf<any>(p.stretches);
    const first = p.stretches?.[0];
    const now = kind === "IN" ? p.current : null;
    const shown = now || last;
    return {
      personId: p.personId,
      code: p.code,
      name: p.name,
      kind,
      work: workLabel(shown?.payBasis, shown?.activity),
      loomCode: kind === "IN" ? lastOf<any>(p.stretches)?.forLoomCode || null : last?.forLoomCode || null,
      firstIn: first?.from || null,
      lastOut: kind === "OUT" ? last?.to || null : null,
      outReason: kind === "OUT" ? last?.endReason || null : null,
      since: now?.since || null,
      minutes: (p.timeMinutes || 0) + (p.pieceMinutes || 0),
      pay: p.pay?.total ?? null,
      noRate: p.pay?.status === "RATE_NOT_CONFIGURED",
      waiting: !!p.waiting,
      self: mine.has(p.personId),
    };
  });
  const notYet: AttendanceRow[] = (data?.notYet || []).map((p: any) => ({
    personId: p.personId,
    code: p.code,
    name: p.name,
    kind: "NOT_YET" as AttendanceKind,
    work: workLabel(p.payBasis, p.activity),
    minutes: 0,
    noRate: false,
    waiting: false,
    lastDay: p.lastDay || null,
    payBasis: p.payBasis || null,
    activityId: p.activity?.id || null,
    self: mine.has(p.personId),
  }));
  return [...came, ...notYet];
};

export const countAttendance = (rows: AttendanceRow[]) => ({
  came: rows.filter((r) => r.kind !== "NOT_YET").length,
  inNow: rows.filter((r) => r.kind === "IN").length,
  notYet: rows.filter((r) => r.kind === "NOT_YET").length,
});

/** The body for a one-tap In: a regular comes back to the work they did last time. */
export const quickInBody = (r: AttendanceRow, date: string, time: string, requestId: string) => ({
  personId: r.personId,
  date,
  time,
  type: "IN",
  ...(r.kind === "NOT_YET" && r.payBasis ? { payBasis: r.payBasis, ...(r.activityId ? { activityId: r.activityId } : {}) } : {}),
  clientRequestId: requestId,
});
